import { useState, useRef } from "react";
import {
  ChevronDown, ChevronUp, X, Mail, ClipboardList, Calendar,
  Star, Lock, Link2, Users, Clock, AlertCircle, Copy, Briefcase, UserCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { RichTextEditor } from "../RichTextEditor";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { EmailAutocomplete } from "../EmailAutocomplete";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

export const DYNAMIC_VARIABLES = [
  { label: "Buyer 1", value: "[Buyer]" },
  { label: "Buyer 1 First Name", value: "[Buyer First Name]" },
  { label: "Buyer 1 Phone", value: "[Buyer Phone]" },
  { label: "Buyer 2", value: "[Buyer 2]" },
  { label: "Buyer 2 First Name", value: "[Buyer 2 First Name]" },
  { label: "Buyer 2 Phone", value: "[Buyer 2 Phone]" },
  { label: "Buyer Agent", value: "[Buyer Agent]" },
  { label: "Buyer Agent First Name", value: "[Buyer Agent First Name]" },
  { label: "Buyer Agent Phone", value: "[Buyer Agent Phone]" },
  { label: "Buyer Agent Assistant", value: "[Buyer Agent Assistant]" },
  { label: "Buyer Agent Assistant First Name", value: "[Buyer Agent Assistant First Name]" },
  { label: "Buyer Agent Assistant Phone", value: "[Buyer Agent Assistant Phone]" },
  { label: "Seller 1", value: "[Seller]" },
  { label: "Seller 1 First Name", value: "[Seller First Name]" },
  { label: "Seller 1 Phone", value: "[Seller Phone]" },
  { label: "Seller 2", value: "[Seller 2]" },
  { label: "Seller 2 First Name", value: "[Seller 2 First Name]" },
  { label: "Seller 2 Phone", value: "[Seller 2 Phone]" },
  { label: "Seller Agent", value: "[Seller Agent]" },
  { label: "Seller Agent First Name", value: "[Seller Agent First Name]" },
  { label: "Seller Agent Phone", value: "[Seller Agent Phone]" },
  { label: "Seller Agent Assistant", value: "[Seller Agent Assistant]" },
  { label: "Seller Agent Assistant First Name", value: "[Seller Agent Assistant First Name]" },
  { label: "Seller Agent Assistant Phone", value: "[Seller Agent Assistant Phone]" },
  { label: "Lender", value: "[Lender]" },
  { label: "Lender First Name", value: "[Lender First Name]" },
  { label: "Lender Phone", value: "[Lender Phone]" },
  { label: "Title Company", value: "[Title Company]" },
  { label: "Title Company Phone", value: "[Title Company Phone]" },
  { label: "Loan Processor", value: "[Loan Processor]" },
  { label: "Loan Processor First Name", value: "[Loan Processor First Name]" },
  { label: "Coordinator", value: "[Coordinator]" },
  { label: "Coordinator First Name", value: "[Coordinator First Name]" },
  { label: "Property Address", value: "[Property Address]" },
  { label: "Closing Date", value: "[Closing Date]" },
  { label: "Contract Date", value: "[Contract Date]" },
  { label: "Purchase Price", value: "[Purchase Price]" },
  { label: "Earnest Money", value: "[Earnest Money]" },
  { label: "Earnest Money Deadline", value: "[Earnest Money Deadline]" },
  { label: "Inspection Deadline", value: "[Inspection Deadline]" },
  { label: "Insurance Deadline", value: "[Insurance Deadline]" },
  { label: "Possession Date", value: "[Possession Date]" },
  { label: "Zillow Link", value: "[Zillow Link]" },
];

export const REFERENCE_DATES = [
  { value: "closing_date", label: "Closing Date" },
  { value: "contract_date", label: "Contract Date" },
  { value: "inspection_deadline", label: "Inspection Deadline" },
  { value: "financing_deadline", label: "Financing Deadline" },
  { value: "appraisal_deadline", label: "Appraisal Deadline" },
  { value: "insurance_deadline", label: "Insurance Deadline" },
  { value: "earnest_money_deadline", label: "Earnest Money Deadline" },
  { value: "possession_date", label: "Possession Date" },
];

export const DETAIL_BOXES = [
  { value: "buyer_info", label: "Buyer Information" },
  { value: "seller_info", label: "Seller Information" },
  { value: "lender_info", label: "Lender Information" },
  { value: "title_info", label: "Title Company" },
  { value: "dates", label: "Key Dates" },
  { value: "financial", label: "Financial Details" },
  { value: "home_warranty", label: "Home Warranty" },
];

export const TRANSACTION_PARTIES = [
  { value: "assigned_coordinator", label: "Assigned Coordinator", icon: UserCheck },
  { value: "manager", label: "Manager", icon: Briefcase },
  { value: "buyer", label: "Buyer 1", icon: Users },
  { value: "buyer2", label: "Buyer 2", icon: Users },
  { value: "seller", label: "Seller 1", icon: Users },
  { value: "seller2", label: "Seller 2", icon: Users },
  { value: "buyer_agent", label: "Buyer Agent", icon: Briefcase },
  { value: "seller_agent", label: "Seller Agent", icon: Briefcase },
  { value: "lender", label: "Lender", icon: Briefcase },
  { value: "title_company", label: "Title Company", icon: Briefcase },
  { value: "loan_processor", label: "Loan Processor", icon: Briefcase },
];

export const DEFAULT_TASK = {
  task_id: "",
  title: "",
  description: "",
  task_type: "task",
  days_offset: 0,
  due_direction: "before",
  reference_date: "closing_date",
  use_business_days: false,
  weekend_shift: "none",
  is_key_date: false,
  is_private: false,
  detail_box_link: "",
  assigned_to_type: "",
  assigned_parties: [],
  email_reminder: true,
  reminder_days_before: 1,
  email_subject: "",
  email_body: "",
  email_recipients: [],
  email_cc: "",
  email_bcc: "",
  email_cc_parties: [],
  email_bcc_parties: [],
  depends_on_task_id: "",
};

const VARIABLE_GROUPS = [
  {
    label: "People",
    items: DYNAMIC_VARIABLES.filter((v) =>
      ["Buyer 1", "Seller 1", "Buyer Agent", "Seller Agent", "Lender", "Title Company", "Loan Processor", "Coordinator", "Buyer 2", "Seller 2", "Seller Agent Assistant", "Buyer Agent Assistant"].some(
        (p) => v.label === p || v.label === `${p} First Name` || v.label === `${p} Phone`
      )
    ),
  },
  {
    label: "Transaction",
    items: DYNAMIC_VARIABLES.filter((v) =>
      ["Property Address", "Closing Date", "Contract Date", "Purchase Price", "Earnest Money", "Earnest Money Deadline", "Inspection Deadline", "Insurance Deadline", "Possession Date", "Zillow Link"].includes(v.label)
    ),
  },
];

const VariableInsertBar = ({ onInsert }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="text-xs h-7 gap-1" data-testid="insert-variable-btn">
        <Copy className="w-3 h-3" />
        Insert Variable
        <ChevronDown className="w-3 h-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="w-[500px]" align="start" data-testid="insert-variable-dropdown">
      {VARIABLE_GROUPS.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <DropdownMenuSeparator />}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">
            {group.label}
          </DropdownMenuLabel>
          {group.items.map((v) => (
            <DropdownMenuItem
              key={v.value}
              className="px-4 py-2.5 cursor-pointer"
              onSelect={() => onInsert(v.value)}
              data-testid={`var-${v.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="font-mono text-sm text-[var(--t-primary)]">{v.value}</span>
            </DropdownMenuItem>
          ))}
        </div>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const TaskEditor = ({ task, groupIndex, taskIndex, taskCount, allTasks, activeUsers, currentUser, contacts = [], transactionPeople = [], onUpdate, onRemove, onMove }) => {
  const [expanded, setExpanded] = useState(!task.title);
  const emailBodyRef = useRef(null);

  const update = (field, value) => onUpdate(groupIndex, taskIndex, { [field]: value });

  const toggleParty = (partyType) => {
    const current = task.assigned_parties || [];
    const exists = current.find((p) => p.party_type === partyType);
    if (exists) {
      update("assigned_parties", current.filter((p) => p.party_type !== partyType));
    } else {
      update("assigned_parties", [...current, { party_type: partyType, remind_in_days: 0 }]);
    }
  };

  const updatePartyReminder = (partyType, days) => {
    const current = task.assigned_parties || [];
    update("assigned_parties", current.map((p) => (p.party_type === partyType ? { ...p, remind_in_days: days } : p)));
  };

  const insertVarInSubject = (variable) => update("email_subject", (task.email_subject || "") + " " + variable);
  const insertVarInBody = (variable) => { if (emailBodyRef.current) emailBodyRef.current.insertText(variable); };

  const toggleEmailRecipient = (partyType) => {
    const current = task.email_recipients || [];
    update("email_recipients", current.includes(partyType) ? current.filter((r) => r !== partyType) : [...current, partyType]);
  };

  const toggleCcParty = (partyType) => {
    const current = task.email_cc_parties || [];
    update("email_cc_parties", current.includes(partyType) ? current.filter((r) => r !== partyType) : [...current, partyType]);
  };

  const toggleBccParty = (partyType) => {
    const current = task.email_bcc_parties || [];
    update("email_bcc_parties", current.includes(partyType) ? current.filter((r) => r !== partyType) : [...current, partyType]);
  };

  const otherTasks = allTasks.filter((t) => t.task_id !== task.task_id && t.title).map((t) => ({ value: t.task_id, label: t.title }));

  return (
    <div className="border border-[var(--t-border)] rounded-lg bg-white" data-testid={`task-editor-${groupIndex}-${taskIndex}`}>
      {/* Task Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[var(--t-bg)] cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex flex-col shrink-0" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="h-3 w-5 flex items-center justify-center text-[var(--t-text-subtle)] hover:text-[var(--t-text)] disabled:opacity-25" disabled={taskIndex === 0} onClick={() => onMove(groupIndex, taskIndex, -1)} data-testid={`move-task-up-${groupIndex}-${taskIndex}`}>
            <ChevronUp className="w-3 h-3" />
          </button>
          <button type="button" className="h-3 w-5 flex items-center justify-center text-[var(--t-text-subtle)] hover:text-[var(--t-text)] disabled:opacity-25" disabled={taskIndex === taskCount - 1} onClick={() => onMove(groupIndex, taskIndex, 1)} data-testid={`move-task-down-${groupIndex}-${taskIndex}`}>
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>
        {task.task_type === "email" ? <Mail className="w-3.5 h-3.5 text-blue-500 shrink-0" /> : <ClipboardList className="w-3.5 h-3.5 text-[var(--t-primary)] shrink-0" />}
        <span className="flex-1 text-sm font-medium text-[var(--t-text)] truncate">{task.title || "Untitled Task"}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {task.is_key_date && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
          {task.is_private && <Lock className="w-3 h-3 text-slate-400" />}
          {task.task_type === "email" && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-200 text-blue-600">Email</Badge>}
          <span className="text-[10px] text-[var(--t-text-muted)] whitespace-nowrap">
            {task.days_offset}d {task.due_direction} {REFERENCE_DATES.find((r) => r.value === task.reference_date)?.label || task.reference_date}
          </span>
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-[var(--t-text-subtle)] hover:text-red-600" onClick={(e) => { e.stopPropagation(); onRemove(groupIndex, taskIndex); }}>
            <X className="w-3.5 h-3.5" />
          </Button>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[var(--t-text-subtle)]" /> : <ChevronDown className="w-3.5 h-3.5 text-[var(--t-text-subtle)]" />}
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="p-4 space-y-4 border-t border-[var(--t-border)]">
          {/* Task Type + Name */}
          <div className="flex gap-3">
            <div className="w-[130px] shrink-0">
              <Label className="text-xs mb-1 block">Task Type</Label>
              <Select value={task.task_type} onValueChange={(v) => update("task_type", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid={`task-type-${groupIndex}-${taskIndex}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="task"><span className="flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Task</span></SelectItem>
                  <SelectItem value="email"><span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs mb-1 block">Task Name <span className="text-red-500">*</span></Label>
              <Input value={task.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g., Send wire fraud warning to buyer..." className="h-9 text-sm" data-testid={`task-title-${groupIndex}-${taskIndex}`} />
            </div>
          </div>

          {/* Flags */}
          <div className="flex items-center gap-5 flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={task.is_key_date} onCheckedChange={(v) => update("is_key_date", !!v)} data-testid={`key-date-${groupIndex}-${taskIndex}`} />
              <span className="text-xs text-[var(--t-text)]">Key Date</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={task.is_private} onCheckedChange={(v) => update("is_private", !!v)} />
              <span className="text-xs text-[var(--t-text)]">Private</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={task.use_business_days} onCheckedChange={(v) => update("use_business_days", !!v)} data-testid={`biz-days-${groupIndex}-${taskIndex}`} />
              <span className="text-xs text-[var(--t-text)]">Business Days</span>
            </label>
          </div>

          {/* Due Date Formula */}
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-3">
            <Label className="text-xs font-semibold flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Due Date Formula</Label>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="w-[70px]">
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Days</Label>
                <Input type="number" min="0" value={task.days_offset} onChange={(e) => update("days_offset", parseInt(e.target.value) || 0)} className="h-9 text-sm text-center font-semibold" data-testid={`days-offset-${groupIndex}-${taskIndex}`} />
              </div>
              <div className="w-[110px]">
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Direction</Label>
                <Select value={task.due_direction} onValueChange={(v) => update("due_direction", v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid={`due-direction-${groupIndex}-${taskIndex}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="before">Before</SelectItem><SelectItem value="after">After</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Reference Date</Label>
                <Select value={task.reference_date} onValueChange={(v) => update("reference_date", v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid={`ref-date-${groupIndex}-${taskIndex}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{REFERENCE_DATES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">If Weekend</Label>
                <Select value={task.weekend_shift} onValueChange={(v) => update("weekend_shift", v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid={`weekend-shift-${groupIndex}-${taskIndex}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't shift</SelectItem>
                    <SelectItem value="next_business_day">Next Business Day</SelectItem>
                    <SelectItem value="previous_business_day">Previous Business Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[10px] text-[var(--t-text-muted)] italic">
              Due {task.days_offset} {task.use_business_days ? "business " : ""}day{task.days_offset !== 1 ? "s" : ""}{" "}
              {task.due_direction} {REFERENCE_DATES.find((r) => r.value === task.reference_date)?.label || "reference date"}
              {task.weekend_shift !== "none" && ` (shifted to ${task.weekend_shift === "next_business_day" ? "next" : "previous"} business day if on weekend)`}
            </p>
          </div>

          {/* Detail Box + Dependency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> Link to Detail Box</Label>
              <Select value={task.detail_box_link || "none"} onValueChange={(v) => update("detail_box_link", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select a detail box" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No link</SelectItem>
                  {DETAIL_BOXES.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Dependent On Task</Label>
              <Select value={task.depends_on_task_id || "none"} onValueChange={(v) => update("depends_on_task_id", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="No dependency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No dependency</SelectItem>
                  {otherTasks.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs mb-1 block">Task Description</Label>
            <RichTextEditor value={task.description || ""} onChange={(val) => update("description", val)} placeholder="Describe what needs to be done..." minHeight="60px" />
          </div>

          {/* Transaction Parties Assignment */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wider">
              <Users className="w-3.5 h-3.5" /> Transaction Parties
              {(task.assigned_parties || []).length > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1.5 ml-1">{(task.assigned_parties || []).length}</Badge>}
            </Label>
            <div className="border border-[var(--t-border)] rounded-md overflow-hidden">
              <div className="grid grid-cols-[1fr_100px] gap-0 bg-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-semibold border-b border-[var(--t-border)]">
                <span>Assigned To</span><span>Remind In</span>
              </div>
              {TRANSACTION_PARTIES.map((party) => {
                const isAssigned = (task.assigned_parties || []).some((p) => p.party_type === party.value);
                const partyData = (task.assigned_parties || []).find((p) => p.party_type === party.value);
                const Icon = party.icon;
                return (
                  <div key={party.value} className={`grid grid-cols-[1fr_100px] gap-0 px-3 py-1.5 border-b last:border-b-0 border-[var(--t-border)] items-center transition-colors ${isAssigned ? "bg-blue-50/50" : "hover:bg-slate-50"}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={isAssigned} onCheckedChange={() => toggleParty(party.value)} data-testid={`party-${party.value}-${groupIndex}-${taskIndex}`} />
                      <Icon className="w-3.5 h-3.5 text-[var(--t-text-muted)]" /><span className="text-xs text-[var(--t-text)]">{party.label}</span>
                    </label>
                    {isAssigned && (
                      <div className="flex items-center gap-1">
                        <Input type="number" min="0" value={partyData?.remind_in_days || 0} onChange={(e) => updatePartyReminder(party.value, parseInt(e.target.value) || 0)} className="h-7 w-14 text-xs text-center" />
                        <span className="text-[10px] text-[var(--t-text-muted)]">days</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {activeUsers.length > 0 && (
                <>
                  <div className="px-3 py-1 bg-slate-100 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-semibold border-b border-t border-[var(--t-border)]">Team Members</div>
                  {activeUsers.map((u) => {
                    const partyKey = `coordinator:${u.user_id}`;
                    const isAssigned = (task.assigned_parties || []).some((p) => p.party_type === partyKey);
                    const partyData = (task.assigned_parties || []).find((p) => p.party_type === partyKey);
                    return (
                      <div key={u.user_id} className={`grid grid-cols-[1fr_100px] gap-0 px-3 py-1.5 border-b last:border-b-0 border-[var(--t-border)] items-center transition-colors ${isAssigned ? "bg-blue-50/50" : "hover:bg-slate-50"}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox checked={isAssigned} onCheckedChange={() => toggleParty(partyKey)} />
                          <Briefcase className="w-3.5 h-3.5 text-[var(--t-text-muted)]" />
                          <span className="text-xs text-[var(--t-text)]">
                            {u.name}{u.role === "super_admin" ? " (Admin)" : ""}{currentUser?.user_id === u.user_id ? <span className="text-[var(--t-primary)] font-semibold ml-1">(me)</span> : ""}
                          </span>
                        </label>
                        {isAssigned && (
                          <div className="flex items-center gap-1">
                            <Input type="number" min="0" value={partyData?.remind_in_days || 0} onChange={(e) => updatePartyReminder(partyKey, parseInt(e.target.value) || 0)} className="h-7 w-14 text-xs text-center" />
                            <span className="text-[10px] text-[var(--t-text-muted)]">days</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Email Reminder */}
          <div className="flex items-center gap-2">
            <Switch checked={task.email_reminder} onCheckedChange={(v) => update("email_reminder", v)} id={`reminder-${groupIndex}-${taskIndex}`} />
            <Label htmlFor={`reminder-${groupIndex}-${taskIndex}`} className="text-xs text-[var(--t-text-muted)]">Email reminder</Label>
            {task.email_reminder && (
              <div className="flex items-center gap-1 ml-2">
                <Input type="number" min="0" value={task.reminder_days_before} onChange={(e) => update("reminder_days_before", parseInt(e.target.value) || 0)} className="h-7 w-14 text-xs text-center" />
                <span className="text-[10px] text-[var(--t-text-muted)]">days before</span>
              </div>
            )}
          </div>

          {/* Email Template */}
          {task.task_type === "email" && (
            <div className="space-y-3 p-3 rounded-md bg-blue-50/60 border border-blue-200">
              <Label className="text-xs font-semibold flex items-center gap-1.5 text-blue-700"><Mail className="w-3.5 h-3.5" /> Email Template</Label>

              {/* Recipients */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">Send To</Label>
                <div className="flex flex-wrap gap-2">
                  {TRANSACTION_PARTIES.map((party) => (
                    <label key={party.value} className="flex items-center gap-1 cursor-pointer">
                      <Checkbox checked={(task.email_recipients || []).includes(party.value)} onCheckedChange={() => toggleEmailRecipient(party.value)} data-testid={`email-recipient-${party.value}-${groupIndex}-${taskIndex}`} />
                      <span className="text-xs">{party.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* CC */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">CC — Transaction Parties</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {TRANSACTION_PARTIES.map((party) => (
                    <label key={party.value} className="flex items-center gap-1 cursor-pointer">
                      <Checkbox checked={(task.email_cc_parties || []).includes(party.value)} onCheckedChange={() => toggleCcParty(party.value)} data-testid={`email-cc-party-${party.value}-${groupIndex}-${taskIndex}`} />
                      <span className="text-xs">{party.label}</span>
                    </label>
                  ))}
                </div>
                <EmailAutocomplete value={task.email_cc || ""} onChange={(val) => update("email_cc", val)} contacts={contacts} transactionPeople={transactionPeople} placeholder="Additional CC emails (type to search contacts...)" className="h-9 text-sm bg-white" data-testid={`email-cc-${groupIndex}-${taskIndex}`} />
              </div>

              {/* BCC */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">BCC — Transaction Parties</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {TRANSACTION_PARTIES.map((party) => (
                    <label key={party.value} className="flex items-center gap-1 cursor-pointer">
                      <Checkbox checked={(task.email_bcc_parties || []).includes(party.value)} onCheckedChange={() => toggleBccParty(party.value)} data-testid={`email-bcc-party-${party.value}-${groupIndex}-${taskIndex}`} />
                      <span className="text-xs">{party.label}</span>
                    </label>
                  ))}
                </div>
                <EmailAutocomplete value={task.email_bcc || ""} onChange={(val) => update("email_bcc", val)} contacts={contacts} transactionPeople={transactionPeople} placeholder="Additional BCC emails (type to search contacts...)" className="h-9 text-sm bg-white" data-testid={`email-bcc-${groupIndex}-${taskIndex}`} />
              </div>

              {/* Subject */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Subject</Label>
                  <VariableInsertBar onInsert={insertVarInSubject} />
                </div>
                <Input value={task.email_subject || ""} onChange={(e) => update("email_subject", e.target.value)} placeholder="e.g., Wire Fraud Warning - [Property Address]" className="h-9 text-sm bg-white" data-testid={`email-subject-${groupIndex}-${taskIndex}`} />
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Email Body</Label>
                  <VariableInsertBar onInsert={insertVarInBody} />
                </div>
                <RichTextEditor ref={emailBodyRef} value={task.email_body || ""} onChange={(val) => update("email_body", val)} placeholder="Dear [Buyer], This is a reminder regarding your transaction at [Property Address]..." minHeight="120px" />
                <p className="text-[10px] text-blue-600 mt-1">
                  Use dynamic variables like [Buyer], [Seller], [Lender], [Property Address], [Closing Date] to personalize emails.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
